import { AtSign, CalendarDays, Clock3, LockKeyhole, Phone, Trash2, UserRoundCheck } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import type { AdminUserRecord } from '@/api/adminAdmins';

interface AdminAccountCardProps {
  admin: AdminUserRecord;
  onDelete: (admin: AdminUserRecord) => void;
}

const dateFormatter = new Intl.DateTimeFormat('el-GR', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

const dateTimeFormatter = new Intl.DateTimeFormat('el-GR', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

function formatDate(value: string | null, withTime = false): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return (withTime ? dateTimeFormatter : dateFormatter).format(date);
}

function protectionCopy(admin: AdminUserRecord): string {
  if (admin.deleteBlockReason === 'self') return 'Ο τρέχων λογαριασμός προστατεύεται';
  if (admin.deleteBlockReason === 'last_active') return 'Απαιτείται δεύτερος ενεργός admin';
  return '';
}

export function AdminAccountCard({ admin, onDelete }: AdminAccountCardProps) {
  const initial = admin.username.trim().charAt(0).toLocaleUpperCase('el-GR') || 'A';

  return (
    <Card className="group flex h-full flex-col overflow-hidden rounded-2xl border-slate-200/80 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition duration-200 hover:border-slate-300 hover:shadow-[0_12px_30px_rgba(15,23,42,0.07)]">
      <CardHeader className="space-y-0 border-b border-slate-100 p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-base font-bold text-white ring-4 ring-slate-100">
              {initial}
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <CardTitle className="truncate text-base font-bold tracking-[-0.01em] text-slate-950">
                  {admin.username}
                </CardTitle>
                {admin.isCurrent && (
                  <Badge className="border-transparent bg-rose-50 text-rose-700 hover:bg-rose-50">Εσείς</Badge>
                )}
              </div>
              <div className="mt-1 flex items-center gap-1.5 text-xs font-medium">
                <span className={`h-2 w-2 rounded-full ${admin.active ? 'bg-emerald-500' : 'bg-slate-300'}`} aria-hidden="true" />
                <span className={admin.active ? 'text-emerald-700' : 'text-slate-500'}>
                  {admin.active ? 'Ενεργός λογαριασμός' : 'Ανενεργός λογαριασμός'}
                </span>
              </div>
            </div>
          </div>
          {admin.isCurrent && <UserRoundCheck className="h-5 w-5 shrink-0 text-rose-600" aria-label="Τρέχων λογαριασμός" />}
        </div>
      </CardHeader>

      <CardContent className="flex-1 space-y-4 p-5">
        <dl className="space-y-3 text-sm">
          <div className="flex min-w-0 items-start gap-3">
            <AtSign className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
            <div className="min-w-0">
              <dt className="sr-only">Email</dt>
              <dd className="truncate">
                <a className="text-slate-700 underline-offset-4 hover:text-rose-700 hover:underline" href={`mailto:${admin.email}`}>
                  {admin.email}
                </a>
              </dd>
            </div>
          </div>
          <div className="flex min-w-0 items-start gap-3">
            <Phone className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
            <div className="min-w-0">
              <dt className="sr-only">Κινητό</dt>
              <dd>
                {admin.mobile ? (
                  <a className="text-slate-700 underline-offset-4 hover:text-rose-700 hover:underline" href={`tel:${admin.mobile}`}>
                    {admin.mobile}
                  </a>
                ) : <span className="text-slate-500">Δεν έχει καταχωρηθεί</span>}
              </dd>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Clock3 className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
            <div>
              <dt className="text-xs font-medium text-slate-400">Τελευταία σύνδεση</dt>
              <dd className={admin.lastLoginAt ? 'mt-0.5 text-slate-700' : 'mt-0.5 font-medium text-amber-700'}>
                {admin.lastLoginAt ? formatDate(admin.lastLoginAt, true) : 'Δεν έχει συνδεθεί ακόμη'}
              </dd>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <CalendarDays className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
            <div>
              <dt className="text-xs font-medium text-slate-400">Δημιουργήθηκε</dt>
              <dd className="mt-0.5 text-slate-700">{formatDate(admin.createdAt)}</dd>
            </div>
          </div>
        </dl>
      </CardContent>

      <CardFooter className="min-h-[68px] border-t border-slate-100 bg-slate-50/70 p-3.5">
        {admin.canDelete ? (
          <Button
            type="button"
            variant="ghost"
            className="h-11 w-full justify-center text-rose-700 hover:bg-rose-50 hover:text-rose-800 focus-visible:ring-rose-500"
            onClick={() => onDelete(admin)}
          >
            <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
            Διαγραφή λογαριασμού
          </Button>
        ) : (
          <div className="flex min-h-11 w-full items-center justify-center gap-2 rounded-xl px-3 text-center text-xs font-medium leading-5 text-slate-500">
            <LockKeyhole className="h-4 w-4 shrink-0" aria-hidden="true" />
            {protectionCopy(admin)}
          </div>
        )}
      </CardFooter>
    </Card>
  );
}
